<!-- src/views/Page.vue -->
<script setup lang="ts">
import { ref, onMounted, watchEffect, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'

import { useContentStore } from '@/stores/content.store'
import { useHeroStore } from '@/stores/hero.store'
import { useUIStore } from '@/stores/ui.store'
import { isNotFoundRoute, notFoundLocationForRoute } from '@/router/notFound'

import {
  getComponentClasses,
  normalizeBlocks,
  resolveComponentByType,
  type PageBlock,
} from '@/utils/components'

const router = useRouter()
const route = useRoute()

const contentStore = useContentStore()
const { pages, isLoading, loading, errors } = storeToRefs(contentStore)
const ui = useUIStore()

const heroStore = useHeroStore()

type PageModel = (typeof pages.value)[number]
const post = ref<PageModel | null>(null)

function resolveFromRoute() {
  // Wait until global init is finished
  if (isLoading.value) return

  // If pages are in local loading (force refresh, etc.)
  if (loading.value.pages) return
  if (!contentStore.hasLoaded('pages')) return
  if (ui.isBackendUnavailable) return
  if (errors.value.pages) return

  const slug = typeof route.params.slug === 'string' ? route.params.slug : ''
  if (!slug) return

  const match = pages.value.find((p) => p.slug === slug)

  if (!match) {
    if (!contentStore.hasFullCoverage('pages')) {
      void contentStore.ensureFullResource('pages')
      return
    }

    // Avoid a redirect loop: do not push if we are already on 404
    if (!isNotFoundRoute(route)) {
      void router.replace(notFoundLocationForRoute(route, { slug }))
    }
    return
  }

  post.value = match
  heroStore.setHeroFromSegment(match)
}

/**
 * components can be undefined/false -> always an array in the template
 */
const componentBlocks = computed<PageBlock[]>(() => {
  const raw = post.value?.components
  return Array.isArray(raw) ? normalizeBlocks(raw) : []
})

onMounted(async () => {
  await contentStore.ensureRouteResource('pages')
})

// Reacts to: isLoading, loading.pages, pages, route.params.slug
watchEffect(() => {
  resolveFromRoute()
})
</script>

<template>
  <keep-alive>
    <component
      v-for="(block, index) in componentBlocks"
      :key="block.id"
      :is="resolveComponentByType(block.type, 'page')"
      :block="block"
      :data="block"
      :data-key="index"
      :data-component="block.type"
      :data-uuid="block.raw.uuid"
      :class="getComponentClasses(block)"
    />
  </keep-alive>
</template>
